import React, { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Edit2, Trash2, Search, Users, Calendar, FolderOpen, CheckCircle } from 'lucide-react';
import { format } from 'date-fns';
import { Tables } from '@/integrations/supabase/types';
import { Staff } from './SchedulerTypes';
import ProjectModal from './ProjectModal';
import { useClinic } from '@/contexts/ClinicContext';

type Project = Tables<'projects'>;
type ProjectStaff = Tables<'project_staff'>;
type StaffTodo = Tables<'staff_todos'>;

interface ProjectWithDetails extends Project {
  projectStaff?: ProjectStaff[];
  tasks?: StaffTodo[];
  pmStaff?: Staff;
}

interface ProjectsViewProps {
  projects: ProjectWithDetails[];
  staff: Staff[];
  onAddProject: (data: { project: Omit<Project, 'id' | 'created_at' | 'updated_at'>; staffIds: string[] }) => void;
  onUpdateProject: (data: { project: Project; staffIds: string[] }) => void;
  onDeleteProject: (id: string) => void;
}

const ProjectsView: React.FC<ProjectsViewProps> = ({
  projects,
  staff,
  onAddProject,
  onUpdateProject,
  onDeleteProject,
}) => {
  const { selectedClinic } = useClinic();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedPM, setSelectedPM] = useState<string>('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<ProjectWithDetails | null>(null);

  const activeStaff = staff.filter(s => s.status === 'active');

  const filteredProjects = useMemo(() => {
    if (!projects || !Array.isArray(projects)) {
      return [];
    }
    
    let filtered = [...projects]; // Create a copy to avoid mutation

    // Filter by clinic - only if not in 'all' view and selectedClinic is set
    if (selectedClinic && selectedClinic !== 'all') {
      filtered = filtered.filter(project => project.clinic_id === selectedClinic);
    }

    // Filter by status
    if (selectedStatus !== 'all') {
      filtered = filtered.filter(project => project.status === selectedStatus);
    }

    // Filter by PM
    if (selectedPM !== 'all') {
      filtered = filtered.filter(project => project.pm_staff_id === selectedPM);
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(project =>
        project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (project.description && project.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Sort by created date (newest first)
    filtered.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

    return filtered;
  }, [projects, selectedClinic, selectedStatus, selectedPM, searchTerm]);

  const handleAddProject = () => {
    setEditingProject(null);
    setIsModalOpen(true);
  };

  const handleEditProject = (project: ProjectWithDetails) => {
    setEditingProject(project);
    setIsModalOpen(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-700';
      case 'on-hold':
        return 'bg-yellow-100 text-yellow-700';
      case 'completed':
        return 'bg-blue-100 text-blue-700';
      case 'cancelled':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getProjectProgress = (project: ProjectWithDetails) => {
    if (!project.tasks || project.tasks.length === 0) return 0;
    const completedTasks = project.tasks.filter(task => task.status === 'completed').length;
    return Math.round((completedTasks / project.tasks.length) * 100);
  };

  return (
    <div className="space-y-4">
      {/* Header and Filters */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">
              Projects {projects && `(${projects.length} total)`}
            </h2>
            <Button onClick={handleAddProject} size="sm">
              <Plus className="h-4 w-4 mr-1" />
              New Project
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Status Filter */}
            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="on-hold">On Hold</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>

            {/* PM Filter */}
            <Select value={selectedPM} onValueChange={setSelectedPM}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by PM" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Project Managers</SelectItem>
                {activeStaff.map(staffMember => (
                  <SelectItem key={staffMember.id} value={staffMember.id}>
                    {staffMember.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Search */}
            <div className="relative lg:col-span-2">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search projects..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Projects Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredProjects.map(project => {
          const progress = getProjectProgress(project);
          const participantCount = project.projectStaff?.length || 0;

          return (
            <Card key={project.id} className="hover:shadow-lg transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg">{project.name}</h3>
                    <Badge className={`${getStatusColor(project.status)} mt-1`}>
                      {project.status}
                    </Badge>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEditProject(project)}
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDeleteProject(project.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {project.description && (
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                    {project.description}
                  </p>
                )}

                <div className="space-y-2 text-sm">
                  {/* PM */}
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-600">PM:</span>
                    <span className="font-medium">
                      {project.pmStaff?.name || 'Unknown'}
                    </span>
                  </div>

                  {/* Participants */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-gray-400" />
                      <span className="text-gray-600">Team:</span>
                      <span className="font-medium">{participantCount} members</span>
                    </div>
                    {project.projectStaff && project.projectStaff.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {project.projectStaff.slice(0, 5).map((ps) => {
                          const staffMember = staff.find(s => s.id === ps.staff_id);
                          if (!staffMember) return null;
                          return (
                            <div
                              key={ps.staff_id}
                              className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-200 text-xs font-medium"
                              title={staffMember.name}
                              style={{ 
                                backgroundColor: staffMember.color ? `${staffMember.color}20` : undefined,
                                color: staffMember.color || undefined
                              }}
                            >
                              {staffMember.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                            </div>
                          );
                        })}
                        {project.projectStaff.length > 5 && (
                          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-200 text-xs font-medium">
                            +{project.projectStaff.length - 5}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Dates */}
                  {(project.start_date || project.end_date) && (
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-gray-400" />
                      <span className="text-gray-600">
                        {project.start_date && format(new Date(project.start_date), 'MMM d')}
                        {project.start_date && project.end_date && ' - '}
                        {project.end_date && format(new Date(project.end_date), 'MMM d, yyyy')}
                      </span>
                    </div>
                  )}

                  {/* Tasks */}
                  <div className="flex items-center gap-2">
                    <FolderOpen className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-600">Tasks:</span>
                    <span className="font-medium">{project.tasks?.length || 0}</span>
                  </div>

                  {/* Progress */}
                  {project.tasks && project.tasks.length > 0 && (
                    <div className="mt-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-gray-600">Progress</span>
                        <span className="text-xs font-medium">{progress}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all"
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {filteredProjects.length === 0 && (
        <div className="text-center py-12">
          <FolderOpen className="h-12 w-12 mx-auto mb-3 text-gray-300" />
          <p className="text-gray-500">No projects found</p>
          {projects && projects.length > 0 && (
            <div className="mt-4 text-sm text-gray-400">
              <p>{projects.length} total projects exist</p>
              <p>Current filters:</p>
              <p>- Clinic: {selectedClinic || 'all'}</p>
              <p>- Status: {selectedStatus}</p>
              <p>- PM: {selectedPM}</p>
              <p>- Search: {searchTerm || 'none'}</p>
            </div>
          )}
        </div>
      )}

      {/* Project Modal */}
      <ProjectModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={(project, staffIds) => {
          if (editingProject) {
            onUpdateProject({ project: { ...editingProject, ...project }, staffIds });
          } else {
            onAddProject({ project, staffIds });
          }
          setIsModalOpen(false);
        }}
        staff={activeStaff}
        project={editingProject}
        selectedClinic={selectedClinic === 'all' ? undefined : selectedClinic}
      />
    </div>
  );
};

export default ProjectsView;